import React, { useState, useEffect, useRef } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import RichTextEditor from "react-rte";

function Sorting({ item, updateItem, lang }) {
  const [answers, setAnswers] = useState(
    item.contents?.a?.length > 0
      ? item.contents.a
      : [
        { id: 0, text: { en: "" }, locked: false },
        { id: 1, text: { en: "" }, locked: false },
      ]
  );
  const [editorState, setEditorState] = useState(
    RichTextEditor.createValueFromString(item.contents?.q ? item.contents?.q[lang.code] : "", "html")
  );
  const posFeedbackRef = useRef();
  const negFeedbackRef = useRef();
  const [clone, setClone] = useState();
  // eslint-disable-next-line no-unused-vars
  const [itemType, setItemType] = useState("text");

  useEffect(() => {
    setEditorState(RichTextEditor.createValueFromString(item.contents?.q?.[lang.code] || "", "html"));
  }, [lang]);

  function handleEditorValueChange(value) {
    setEditorState(value);
    let temp = item;

    if (value.toString("html") === "<p><br></p>") {
      temp.contents.q[lang.code] = "";
    } else {
      temp.contents.q[lang.code] = value.toString("html");
    }
    updateItem({ ...temp });
  }

  function modifyAnswersLength(count) {
    let temp = answers.slice();

    if (count > answers.length) {
      let amountToAdd = count - answers.length;
      // if we're adding a question
      // add as many blank answers as needed
      for (let x = 0; x < amountToAdd; x++) {
        temp.push({ id: answers.length, text: { en: "" }, locked: false });
      }
    } else {
      // if we're removing one
      let amountToRemove = answers.length - count;
      for (let x = 0; x < amountToRemove; x++) {
        temp.pop();
      }
    }

    setAnswers(temp);

    updateItem({
      ...item,
      contents: {
        ...item.contents,
        a: temp,
      },
    });
  }

  function updateAnswer(id, text, locked) {
    let temp = answers.map((a) => {
      if (a.id === id) {
        const tempAnswer = a.text;
        tempAnswer[lang.code] = text;

        return {
          ...a,
          text: tempAnswer,
          locked,
        };
      } else return a;
    });
    setAnswers(temp);
    updateItem({
      ...item,
      contents: {
        ...item.contents,
        a: temp,
      },
    });
  }

  function updateFeedback() {
    let temp = { ...item.contents.feedback };

    // if the corresponding text field is empty, don't even create the object key
    temp[lang.code] = {
      ...(posFeedbackRef.current.value && { pos: posFeedbackRef.current.value }),
      ...(negFeedbackRef.current.value && { neg: negFeedbackRef.current.value }),
    };

    updateItem({
      ...item,
      // if the resulting temp object is empty (no pos or neg feedback), send a nullish value
      contents: {
        ...item.contents,
        feedback: Object.keys(temp).length === 0 ? null : temp,
      },
    });
  }

  function updateType(e) {
    setItemType(e.target.value);
    let temp = item;
    temp.contents.itemType = e.target.value;
  }

  function rearrangeItems(result) {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    const newAnswers = reorder(answers, result.source.index, result.destination.index);
    setAnswers(newAnswers);
  }

  function reorder(list, startIndex, endIndex) {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    result.forEach((q, i) => (q.id = i)); // reorder IDs

    return result;
  }

  function handleDragStart(start) {
    setClone(answers[parseInt(start.draggableId)]);
  }

  return (
    <div className="QM-question__contents-editor" id={item.type}>
      <div className="QM-question__main-properties">
        <div className="QM-question__question">
          <p>Question</p>
          <RichTextEditor value={editorState} onChange={handleEditorValueChange} />
        </div>
        <div className="QM-question__answer-list">
          <div className="QM-question__answer-list-label">
            <p>Answers</p>
            <div>
              <label>
                Count:{" "}
                <input
                  type="number"
                  min={2}
                  // max={5}
                  value={answers.length}
                  onChange={(e) => modifyAnswersLength(e.target.value)} // prevents user from typing in any value outside of range
                />
              </label>
            </div>
          </div>
          <DragDropContext onBeforeDragStart={handleDragStart} onDragEnd={rearrangeItems}>
            <Droppable
              droppableId="sorting-answers"
              renderClone={(provided) => (
                <tr
                  className="sorting-answer-table-row"
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                  ref={provided.innerRef}
                >
                  <Answer
                    lang={lang}
                    answer={clone}
                    updateAnswer={updateAnswer}
                    index={clone.id}
                    handleProps={provided.dragHandleProps}
                  />
                </tr>
              )}
            >
              {(provided, snapshot) => (
                <table
                  className="QM__answer-table"
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                >
                  <thead>
                    <tr>
                      <th>Position</th>
                      <th>
                        Text or link{" "}
                        <div className="DD__radio-container">
                          <label>
                            <input
                              type="radio"
                              name="sortingItemType"
                              value="text"
                              checked={item.contents?.itemType === "text"}
                              onChange={updateType}
                            />{" "}
                            Text
                          </label>
                          <label>
                            <input
                              type="radio"
                              name="sortingItemType"
                              value="image"
                              checked={item.contents?.itemType === "image"}
                              onChange={updateType}
                            />{" "}
                            Image
                          </label>
                          <label>
                            <input
                              type="radio"
                              name="sortingItemType"
                              value="audio"
                              checked={item.contents?.itemType === "audio"}
                              onChange={updateType}
                            />{" "}
                            Audio
                          </label>
                          <label>
                            <input
                              type="radio"
                              name="sortingItemType"
                              value="video"
                              checked={item.contents?.itemType === "video"}
                              onChange={updateType}
                            />{" "}
                            Video
                          </label>
                        </div>
                      </th>
                      <th>
                        <span className="title-tooltip" title="Choose which elements will NOT be shuffled.">
                          Lock
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {answers.map((a, i) => (
                      <Draggable key={i} draggableId={String(a.id)} index={i}>
                        {(provided) => (
                          <tr className="sorting-answer-table-row" ref={provided.innerRef} {...provided.draggableProps}>
                            <Answer
                              key={a.id}
                              lang={lang}
                              answer={a}
                              updateAnswer={updateAnswer}
                              index={i}
                              handleProps={provided.dragHandleProps}
                            />
                          </tr>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </tbody>
                </table>
              )}
            </Droppable>
          </DragDropContext>
        </div>
        <div className="QM__feedback-options">
          <p>Feedback (optional)</p>
          <label htmlFor="correct-answer-feedback">Correct: </label>
          <input
            ref={posFeedbackRef}
            id="correct-answer-feedback"
            type="text"
            placeholder={`E.g. "That's right! In fact, tardigrades can survive even in the cold vacuum of space!"`}
            value={item.contents.feedback?.[lang.code]?.pos || ""}
            onChange={updateFeedback}
          />
          <label htmlFor="incorrect-answer-feedback">Incorrect: </label>
          <input
            ref={negFeedbackRef}
            id="incorrect-answer-feedback"
            type="text"
            placeholder={`E.g. "Sorry! Remember that the dividend goes in the 'house' when doing long division."`}
            value={item.contents.feedback?.[lang.code]?.neg || ""}
            onChange={updateFeedback}
          />
        </div>
      </div>
    </div>
  );
}

function Answer({ lang, answer: { id, text, locked }, updateAnswer, index, handleProps }) {
  const textRef = useRef();
  const lockedRef = useRef();

  function handleUpdate() {
    updateAnswer(id, textRef.current.value, lockedRef.current.checked);
  }

  return (
    <>
      <td className="td-answer-index">
        <div>
          <div className="sorting-answer-drag-handle" {...handleProps}>
            <svg viewBox="0 0 24 24">
              <path
                fill="none"
                stroke="#aaa"
                strokeWidth="3"
                strokeLinecap="round"
                d="M3.676 5.765h16.648M3.676 12h16.648M3.676 18.235h16.648"
              />
            </svg>
          </div>
          {index + 1}.
        </div>
      </td>
      <td className="td-answer-text">
        <input ref={textRef} type="text" value={text[lang.code] || ""} onChange={handleUpdate} />
      </td>
      <td className="td-answer-locked">
        <div className="checkbox-wrapper">
          <input ref={lockedRef} type="checkbox" checked={locked} onChange={handleUpdate} />
        </div>
      </td>
    </>
  );
}

export default Sorting;
